import { useRef, useState } from 'react';

import { aiExpressionAuthoringApi } from 'app/services/api';
import _ from 'lodash';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { GENERATE_PROGRESS_POLL_INTERVAL_MS } from '../GenerateExpression.constants';

const useGenerateExpressionProgress = ({ requestId, isActive }) => {
  const [events, setEvents] = useState([]);
  const abortControllerRef = useRef(null);
  const timerRef = useRef(null);

  useEffectSafeStrictMode(() => {
    if (!isActive || _.isEmpty(requestId)) {
      setEvents([]);

      return _.noop;
    }

    const cancellation = { cancelled: false };

    const poll = () => {
      if (cancellation.cancelled) {
        return;
      }

      if (!_.isNil(abortControllerRef.current)) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      aiExpressionAuthoringApi
        .getExpressionGenerationProgress({ requestId, signal: controller.signal })
        .then(({ data }) => {
          if (cancellation.cancelled || controller.signal.aborted) {
            return;
          }

          const nextEvents = data?.events ?? [];
          setEvents(prevEvents =>
            prevEvents.length === nextEvents.length ? prevEvents : nextEvents,
          );
        })
        .catch(_.noop);
    };

    poll();
    timerRef.current = setInterval(poll, GENERATE_PROGRESS_POLL_INTERVAL_MS);

    return () => {
      cancellation.cancelled = true;

      if (!_.isNil(timerRef.current)) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (!_.isNil(abortControllerRef.current)) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [requestId, isActive]);

  return events;
};

export default useGenerateExpressionProgress;
